import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { ApiConstants } from '../../common/constants/api-url.constant';
import { Merchant } from '../../common/models/shops.model';
import { Pageable } from '../../common/models/pageable.model';
import { Country } from '../../common/models/country.model';
import { Category } from '../../common/models/category.model';
import { BusinessType } from '../../common/models/business-type.model';
import { AnnualSales } from '../../common/models/annual-sales.model';
import { AverageOrderValue } from '../../common/models/average-order.model';
import { Platform } from '../../common/models/platform.model';
import { PartnerRequest } from '../../common/models/partner-request.model';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private http = inject(HttpClient);
  private storageService = inject(LocalStorageService);
  private apiConstants = inject(ApiConstants);

  /** Get paginated merchant list safely */
  getShopsList(params?: string): Observable<Pageable<Merchant>> {
    return this.http.get<Pageable<Merchant>>(this.apiConstants.GET_MERCHANT(params)).pipe(
      map((res) => res ?? { records: [], currentPage: 0, totalPages: 0, totalRecords: 0 }),
      catchError((err) => {
        console.error('Error fetching shops list:', err);
        return of({ records: [], currentPage: 0, totalPages: 0, totalRecords: 0 });
      })
    );
  }

  /** Get country list safely */
  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(this.apiConstants.GET_COUNTRY()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching countries:', err);
        return of([]);
      })
    );
  }

  /** Get category list safely */
  getCategories(): Observable<Category[]> {
    return this.http.get<Category[]>(this.apiConstants.GET_CATEGORY()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching categories:', err);
        return of([]);
      })
    );
  }

  /** Get business types safely */
  getBusinessType(): Observable<BusinessType[]> {
    return this.http.get<BusinessType[]>(this.apiConstants.GET_BUSINESS_TYPE()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching business types:', err);
        return of([]);
      })
    );
  }

  /** Get annual sales safely */
  getAnnualSales(): Observable<AnnualSales[]> {
    return this.http.get<AnnualSales[]>(this.apiConstants.GET_ANNUAL_SALES()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching annual sales:', err);
        return of([]);
      })
    );
  }

  /** Get average order values safely */
  getAverageOrder(): Observable<AverageOrderValue[]> {
    return this.http.get<AverageOrderValue[]>(this.apiConstants.GET_AVERAGE_ORDER_VALUE()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching average orders:', err);
        return of([]);
      })
    );
  }

  /** Get platform list safely */
  getPlatform(): Observable<Platform[]> {
    return this.http.get<Platform[]>(this.apiConstants.GET_PLATFORM()).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching platforms:', err);
        return of([]);
      })
    );
  }

  /** Merchant signup safely */
  merchantSignUp(request: PartnerRequest): Observable<Merchant | null> {
    return this.http.post<Merchant>(this.apiConstants.MERCHANT_INFO(), request).pipe(
      catchError((err) => {
        console.error('Error signing up merchant:', err);
        return of(null);
      })
    );
  }
  merchantSignUpV2(request: PartnerRequest): Observable<Merchant | null> {
    return this.http.post<Merchant>(this.apiConstants.MERCHANT_INFO_V2(), request).pipe(
      catchError((err) => {
        console.error('Error signing up merchant:', err);
        return throwError(() => err);
      })
    );
  }

  /** Upload document to interest request */
  uploadDocuments(
    interestRequestId: any,
    documentType: string,
    file: File,
    tokens: { editToken?: string | null; completionToken?: string | null } = {}
  ): Observable<any> {
    const url = this.apiConstants.UPLOAD_DOCUMENTS(interestRequestId);

    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', file, file.name);

    return this.http.post<any>(url, formData).pipe(
      catchError((err) => {
        console.error('Document upload error:', err);
        return throwError(() => err);
      })
    );
  }

  /** Get all documents for an interest request */
  getDocuments(interestRequestId: number): Observable<any[]> {
    const url = this.apiConstants.GET_INTEREST_REQUEST_DOCUMENTS(interestRequestId.toString());
    return this.http.get<any[]>(url).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching documents:', err);
        return of([]);
      })
    );
  }

  /** Delete a document from interest request */
  deleteDocument(interestRequestId: number, documentId: number): Observable<void> {
    const url = this.apiConstants.DELETE_INTEREST_REQUEST_DOCUMENT(
      interestRequestId.toString(),
      documentId
    );
    return this.http.delete<void>(url).pipe(
      catchError((err) => {
        console.error('Error deleting document:', err);
        return throwError(() => err);
      })
    );
  }

  /** Get application details by completion token */
  getApplicationByToken(token: string): Observable<any> {
    const url = this.apiConstants.GET_APPLICATION_BY_TOKEN(token);
    return this.http.get<any>(url).pipe(
      catchError((err) => {
        console.error('Error fetching application by token:', err);
        return throwError(() => err);
      })
    );
  }

  /** Get documents by token (public endpoint for complete-application flow) */
  getDocumentsByToken(token: string): Observable<any[]> {
    const url = this.apiConstants.GET_DOCUMENTS_BY_TOKEN(token);
    return this.http.get<any[]>(url).pipe(
      map((data) => data ?? []),
      catchError((err) => {
        console.error('Error fetching documents by token:', err);
        return of([]);
      })
    );
  }

  /** Complete/update application using token (PUT request with only changed fields) */
  completeApplication(token: string, data: any): Observable<any> {
    const url = this.apiConstants.COMPLETE_APPLICATION(token);
    return this.http.put<any>(url, data).pipe(
      catchError((err) => {
        console.error('Error completing application:', err);
        return throwError(() => err);
      })
    );
  }

  /** Update interest request by ID (for 3-step signup flow) */
  updateInterestRequest(interestRequestId: number, data: PartnerRequest): Observable<any> {
    const url = this.apiConstants.UPDATE_INTEREST_REQUEST(interestRequestId);
    return this.http.put<any>(url, data).pipe(
      catchError((err) => {
        console.error('Error updating interest request:', err);
        return throwError(() => err);
      })
    );
  }
}
